import React from 'react';
import PropTypes from 'prop-types';
import { DateRange } from 'react-date-range';
import './DateRangePicker.css';
import {
  deserializeYesplanAPIDate,
  getMonthNames,
  isDateObjectOfCorrectObjectType
} from '../../libs/dateTimeUtilities';
import {
  StyledDateRangePicker,
  StyledDateNavigationWrapper,
  StyledSelectMonthYearWrapper,
  StyledMonthSelectWrapper,
  StyledYearSelectWrapper
} from './DateRangePicker.style';
import Button from '../Button/Button';
import Select from '../Select/Select';
import { Body } from '../../styled components/Typography';

const renderNavigator = (focusedDate, changeShownDate, dateRangeProps) => {
  const { showMonthArrow, minDate, maxDate, showMonthAndYearPickers, ariaLabels } =
    dateRangeProps;
  const monthNames = getMonthNames();
  const upperYearLimit = maxDate.getFullYear();
  const lowerYearLimit = minDate.getFullYear();

  const selectedMonth = {
    value: focusedDate.getMonth(),
    text: monthNames[focusedDate.getMonth()]
  };

  const selectedYear = {
    value: focusedDate.getFullYear(),
    text: `${focusedDate.getFullYear()}`
  };

  const options = monthNames.map((monthName, i) => ({
    value: i,
    text: monthName
  }));

  const yearOptions = new Array(upperYearLimit - lowerYearLimit + 1)
    .fill(upperYearLimit)
    .map((val, i) => ({
      value: val - i,
      text: `${val - i}`
    }))
    .reverse();

  const showMonthAsSelect = true;
  const showYearAsSelect = yearOptions.length > 1;

  return (
    <StyledDateNavigationWrapper onMouseUp={(e) => e.stopPropagation()}>
      {showMonthArrow ? (
        <Button
          contentType="icon"
          data-test-daterange-prev-button
          onClick={() => changeShownDate(-1, 'monthOffset')}
          aria-label={ariaLabels.prevButton || i18n('Previous')}
          icon="navigate-before"
          size="medium"
        />
      ) : null}
      {showMonthAndYearPickers ? (
        <StyledSelectMonthYearWrapper>
          {showMonthAsSelect ? (
            <StyledMonthSelectWrapper>
              <Select
                name="rdrMonthPickerSelect"
                selected={selectedMonth}
                options={options}
                value={focusedDate.getMonth()}
                onChange={(e) => {
                  changeShownDate(e.value, 'setMonth');
                }}
                aria-label={ariaLabels.monthPicker || i18n('Pick a month')}
              />
            </StyledMonthSelectWrapper>
          ) : (
            <Body tw="">{monthNames[focusedDate.getMonth()]}</Body>
          )}
          {showYearAsSelect ? (
            <StyledYearSelectWrapper>
              <Select
                name="rdrYearPickerSelect"
                selected={selectedYear}
                options={yearOptions}
                value={focusedDate.getFullYear()}
                onChange={(e) => changeShownDate(e.value, 'setYear')}
                aria-label={ariaLabels.yearPicker || i18n('Pick a year')}
              />
            </StyledYearSelectWrapper>
          ) : (
            <Body tw="ml-8">{focusedDate.getFullYear()}</Body>
          )}
        </StyledSelectMonthYearWrapper>
      ) : (
        <div tw="w-full text-center">
          <Body>
            {monthNames[focusedDate.getMonth()]} {focusedDate.getFullYear()}
          </Body>
        </div>
      )}
      {showMonthArrow ? (
        <Button
          contentType="icon"
          data-test-daterange-next-button
          onClick={() => changeShownDate(+1, 'monthOffset')}
          aria-label={ariaLabels.nextButton || i18n('Next')}
          icon="navigate-next"
          size="medium"
        />
      ) : null}
    </StyledDateNavigationWrapper>
  );
};

const DateRangePicker = ({
  dateRange,
  onChange,
  minDate = deserializeYesplanAPIDate('2010-01-01'),
  maxDate = deserializeYesplanAPIDate('2025-12-31'),
  showMonthArrow = true,
  showMonthAndYearPickers = true
}) => {
  dateRange.forEach((dateRangeObject) =>
    Object.keys(dateRangeObject).forEach((key) => {
      if (isDateObjectOfCorrectObjectType(dateRangeObject[key]))
        dateRangeObject[key] = dateRangeObject[key].toDate();
    })
  );
  return (
    <StyledDateRangePicker>
      <DateRange
        onChange={(item) => {
          onChange([item.selection]);
        }}
        months={1}
        minDate={minDate.toDate()}
        maxDate={maxDate.toDate()}
        fixedHeight
        ranges={dateRange}
        showDateDisplay={false}
        weekStartsOn={1}
        showMonthArrow={showMonthArrow}
        showMonthAndYearPickers={showMonthAndYearPickers}
        /* If you want to be able to inspect inside the date picker,
                 comment dragSelectionEnabled={false} */
        dragSelectionEnabled={false}
        navigatorRenderer={renderNavigator}
        weekdayDisplayFormat="iiiiii"
      />
    </StyledDateRangePicker>
  );
};

DateRangePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showMonthArrow: PropTypes.bool,
  minDate: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      toDate: PropTypes.func.isRequired
    })
  ]),
  maxDate: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      toDate: PropTypes.func.isRequired
    })
  ]),
  showMonthAndYearPickers: PropTypes.bool
};

export default DateRangePicker;
